// Popover — flotante hover-based simple sin dependencias.
//
// Pensado para tooltips ricos (no solo texto): el contenido se mantiene visible
// mientras el mouse está sobre el trigger O sobre el contenido, con un grace
// period para mover el cursor entre los dos sin que se cierre.
//
// Posicionamiento FIXED medido desde el trigger: un popover absolute dentro
// de un contenedor con overflow (tablas con scroll) queda RECORTADO por ese
// contenedor aunque haga flip. Fixed escapa del recorte y el left se clampa
// al viewport, así el cuadro cae hacia abajo y nunca se sale de pantalla.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '../../lib/utils';

export interface PopoverProps {
  trigger: ReactNode;
  content: ReactNode;
  /** Delay en ms antes de mostrar (default 200). */
  openDelay?: number;
  /** Delay en ms antes de cerrar tras leave (default 150). */
  closeDelay?: number;
  /** Ancho del popover (default 280px). */
  width?: number;
  className?: string;
}

export function Popover({
  trigger,
  content,
  openDelay = 200,
  closeDelay = 150,
  width = 280,
  className,
}: PopoverProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function scheduleOpen() {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (open) return;
    openTimerRef.current = setTimeout(() => setOpen(true), openDelay);
  }
  function scheduleClose() {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    closeTimerRef.current = setTimeout(() => setOpen(false), closeDelay);
  }

  // Al abrir: posición fija bajo el trigger, con el left clampado al viewport.
  useEffect(() => {
    if (!open || !wrapperRef.current) {
      setPos(null);
      return;
    }
    const rect = wrapperRef.current.getBoundingClientRect();
    setPos({
      left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
      top: rect.bottom + 4,
    });
  }, [open, width]);

  // Cleanup timers
  useEffect(() => {
    return () => {
      if (openTimerRef.current) clearTimeout(openTimerRef.current);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  return (
    <div
      ref={wrapperRef}
      className="relative inline-block"
      onMouseEnter={scheduleOpen}
      onMouseLeave={scheduleClose}
    >
      {trigger}
      {open && pos ? (
        <div
          className={cn(
            'fixed z-50 rounded-md border border-border bg-surface-elevated p-3 shadow-xl',
            className,
          )}
          style={{ width, left: pos.left, top: pos.top }}
          onMouseEnter={scheduleOpen}
          onMouseLeave={scheduleClose}
        >
          {content}
        </div>
      ) : null}
    </div>
  );
}
